/**
 * MindMap Gemini AI Service
 *
 * Core AI mechanics for the Second Brain application:
 * - Vector embedding generation
 * - Semantic connection analysis
 * - Image description (Vision)
 * - URL content extraction
 * - RAG-based chat
 * - Gap analysis & Ghost nodes
 * - Devil's Advocate challenges
 *
 * All AI calls are routed through a Supabase Edge Function
 * to keep the API key secure.
 */

import { supabase } from './supabaseService';

// Embedding dimension (must match database schema)
export const EMBEDDING_DIMENSION = 768;

/**
 * Ensure we have a valid session before calling edge functions
 * Refreshes the session if needed
 * @returns {Promise<void>}
 */
async function ensureValidSession() {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError) {
    console.error('Session error:', sessionError);
    throw new Error('Authentication error. Please sign in again.');
  }

  if (!session) {
    throw new Error('Not authenticated. Please sign in to use AI features.');
  }

  // Check if the token is expired or about to expire (within 60 seconds)
  const expiresAt = session.expires_at;
  const now = Math.floor(Date.now() / 1000);

  if (expiresAt && expiresAt - now < 60) {
    // Token is expired or about to expire, refresh it
    const { error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
      console.error('Session refresh error:', refreshError);
      throw new Error('Session expired. Please sign in again.');
    }
  }
}

/**
 * Call the Gemini edge function
 * @param {string} action - The action to perform
 * @param {Object} payload - The data to send
 * @returns {Promise<Object>} - Response from the edge function
 */
async function callGeminiFunction(action, payload) {
  // Ensure we have a valid session before calling the edge function
  await ensureValidSession();

  const { data, error } = await supabase.functions.invoke('gemini', {
    body: { action, payload },
  });

  if (error) {
    console.error(`Edge function error (${action}):`, error);

    // Handle specific error cases
    if (error.message?.includes('401') || error.message?.includes('Invalid JWT') || error.message?.includes('Unauthorized')) {
      // Try to refresh session and retry once
      try {
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          throw new Error('Session expired. Please sign in again.');
        }

        // Retry the request
        const { data: retryData, error: retryError } = await supabase.functions.invoke('gemini', {
          body: { action, payload },
        });

        if (retryError) {
          throw new Error(`AI operation failed after retry: ${retryError.message}`);
        }

        if (retryData.error) {
          throw new Error(`AI operation failed: ${retryData.error}`);
        }

        return retryData;
      } catch (retryErr) {
        throw new Error('Authentication failed. Please sign out and sign in again.');
      }
    }

    throw new Error(`AI operation failed: ${error.message}`);
  }

  if (data.error) {
    console.error(`Gemini error (${action}):`, data.error);
    throw new Error(`AI operation failed: ${data.error}`);
  }

  return data;
}

/**
 * Generate vector embedding for text content
 * @param {string} content - The text to embed
 * @returns {Promise<number[]>} - 768-dimensional vector
 */
export async function generateEmbedding(content) {
  try {
    const result = await callGeminiFunction('embed', { content });

    if (result.embedding.length !== EMBEDDING_DIMENSION) {
      console.warn(`Embedding dimension mismatch: got ${result.embedding.length}, expected ${EMBEDDING_DIMENSION}`);
    }

    return result.embedding;
  } catch (error) {
    console.error('Error generating embedding:', error);
    throw new Error(`Failed to generate embedding: ${error.message}`);
  }
}

/**
 * Analyze connections between a new node and existing similar nodes
 * This is the core "reasoning" function that determines how ideas relate
 *
 * @param {Object} newNode - The newly added node
 * @param {Array} similarNodes - Array of semantically similar nodes from vector search
 * @returns {Promise<Object>} - Object with connections, suggestedCluster, and insights
 */
export async function analyzeConnections(newNode, similarNodes) {
  if (!similarNodes || similarNodes.length === 0) {
    return { connections: [], suggestedCluster: null, insights: null };
  }

  try {
    const result = await callGeminiFunction('analyze-connections', {
      newNode,
      similarNodes,
    });

    return {
      connections: result.connections || [],
      suggestedCluster: result.suggestedCluster,
      insights: result.insights,
    };
  } catch (error) {
    console.error('Error analyzing connections:', error);
    return { connections: [], suggestedCluster: null, insights: null };
  }
}

/**
 * Describe an image using Gemini Vision
 * @param {string} imageBase64 - Base64 encoded image data
 * @param {string} mimeType - Image MIME type (e.g., 'image/jpeg')
 * @returns {Promise<Object>} - Description and extracted concepts
 */
export async function describeImage(imageBase64, mimeType = 'image/jpeg') {
  try {
    return await callGeminiFunction('describe-image', {
      imageBase64,
      mimeType,
    });
  } catch (error) {
    console.error('Error describing image:', error);
    throw new Error(`Failed to describe image: ${error.message}`);
  }
}

/**
 * Extract and summarize content from a URL
 *
 * @param {string} url - The source URL
 * @param {string} scrapedContent - The raw scraped HTML/text content
 * @returns {Promise<Object>} - Summary and extracted information
 */
export async function summarizeUrlContent(url, scrapedContent) {
  try {
    return await callGeminiFunction('summarize-url', {
      url,
      content: scrapedContent,
    });
  } catch (error) {
    console.error('Error summarizing URL content:', error);
    throw new Error(`Failed to summarize URL: ${error.message}`);
  }
}

/**
 * Chat with Brain - RAG-based Q&A on user's knowledge base
 *
 * @param {string} query - User's question
 * @param {Array} contextNodes - Relevant nodes retrieved via vector search
 * @param {Array} chatHistory - Previous messages in the conversation
 * @returns {Promise<Object>} - AI response and source references
 */
export async function chatWithBrain(query, contextNodes, chatHistory = []) {
  try {
    return await callGeminiFunction('chat', {
      query,
      contextNodes,
      chatHistory,
    });
  } catch (error) {
    console.error('Error in chat with brain:', error);
    throw new Error(`Failed to process chat: ${error.message}`);
  }
}

/**
 * Devil's Advocate - Generate a challenge to the user's input
 * Highlights risks, counter-arguments, or logical fallacies
 *
 * @param {Object} node - The node to challenge
 * @param {Array} contextNodes - Related nodes for context
 * @returns {Promise<Object|null>} - Challenge node content or null if no challenge warranted
 */
export async function generateChallenge(node, contextNodes = []) {
  try {
    return await callGeminiFunction('challenge', {
      node,
      contextNodes,
    });
  } catch (error) {
    console.error('Error generating challenge:', error);
    return null;
  }
}

/**
 * Gap Analysis - Identify missing concepts in the map
 * Suggests "Ghost Nodes" that could strengthen the knowledge graph
 *
 * @param {Array} allNodes - All nodes in the current map
 * @param {Array} edges - All edges in the current map
 * @returns {Promise<Object>} - Object with ghostNodes array and assessment
 */
export async function analyzeGaps(allNodes, edges) {
  if (allNodes.length < 3) {
    return { ghostNodes: [], assessment: null };
  }

  try {
    return await callGeminiFunction('analyze-gaps', {
      allNodes,
      edges,
    });
  } catch (error) {
    console.error('Error analyzing gaps:', error);
    return { ghostNodes: [], assessment: null };
  }
}

/**
 * Generate cluster/topic tag for a node
 *
 * @param {string} content - Node content
 * @param {Array} existingClusters - Existing cluster tags in the map
 * @returns {Promise<string>} - Suggested cluster tag
 */
export async function generateClusterTag(content, existingClusters = []) {
  try {
    const result = await callGeminiFunction('cluster-tag', {
      content,
      existingClusters,
    });
    return result.tag;
  } catch (error) {
    console.error('Error generating cluster tag:', error);
    return 'uncategorized';
  }
}

/**
 * Analyze cross-map connections when a submap is included
 *
 * @param {Array} parentMapNodes - Nodes from the parent map
 * @param {Array} subMapNodes - Nodes from the submap being included
 * @returns {Promise<Object>} - Object with connections array and synergies
 */
export async function analyzeCrossMapConnections(parentMapNodes, subMapNodes) {
  try {
    return await callGeminiFunction('cross-map', {
      parentMapNodes,
      subMapNodes,
    });
  } catch (error) {
    console.error('Error analyzing cross-map connections:', error);
    return { connections: [], synergies: null };
  }
}

/**
 * Summarize PDF text content
 * @param {string} pdfText - The extracted text from the PDF
 * @param {string} title - The PDF title/filename
 * @returns {Promise<Object>} - Summary and key points
 */
export async function summarizePDF(pdfText, title = 'PDF Document') {
  try {
    return await callGeminiFunction('summarize-pdf', {
      content: pdfText,
      title,
    });
  } catch (error) {
    console.error('Error summarizing PDF:', error);
    throw new Error(`Failed to summarize PDF: ${error.message}`);
  }
}

/**
 * Parse a natural language cashflow command into structured data
 * @param {string} command - The natural language command (e.g., "Her ayın 15inde maaş gelecek 45000 TL")
 * @param {string} referenceDate - Reference date for relative date calculations (ISO string)
 * @returns {Promise<Object>} - Structured cashflow entry data
 */
export async function parseCashflowCommand(command, referenceDate) {
  try {
    return await callGeminiFunction('parse-cashflow', {
      command,
      referenceDate,
    });
  } catch (error) {
    console.error('Error parsing cashflow command:', error);
    throw new Error(`Komut anlaşılamadı: ${error.message}`);
  }
}

/**
 * Parse a payment schedule from PDF or Excel content
 * @param {string} content - The extracted text content from the file
 * @param {string} fileName - Original file name
 * @param {string} fileType - File type (pdf, excel)
 * @returns {Promise<Object>} - Parsed payment schedule with entries
 */
export async function parsePaymentSchedule(content, fileName, fileType) {
  try {
    return await callGeminiFunction('parse-payment-schedule', {
      content,
      fileName,
      fileType,
    });
  } catch (error) {
    console.error('Error parsing payment schedule:', error);
    throw new Error(`Ödeme takvimi çıkarılamadı: ${error.message}`);
  }
}

/**
 * Parse a payment schedule from an image using Gemini Vision
 * @param {string} imageBase64 - Base64 encoded image data
 * @param {string} mimeType - Image MIME type (e.g., 'image/png', 'image/jpeg')
 * @param {string} fileName - Original file name
 * @returns {Promise<Object>} - Parsed payment schedule with entries
 */
export async function parsePaymentScheduleFromImage(imageBase64, mimeType, fileName) {
  try {
    return await callGeminiFunction('parse-payment-schedule-image', {
      imageBase64,
      mimeType,
      fileName,
    });
  } catch (error) {
    console.error('Error parsing payment schedule from image:', error);
    throw new Error(`Görseldan ödeme takvimi çıkarılamadı: ${error.message}`);
  }
}

/**
 * Analyze cashflow data and provide financial insights
 * @param {Object} data - Cashflow data including entries, summary, etc.
 * @returns {Promise<Object>} - Financial analysis with insights and recommendations
 */
export async function analyzeCashflow(data) {
  try {
    return await callGeminiFunction('analyze-cashflow', data);
  } catch (error) {
    console.error('Error analyzing cashflow:', error);
    throw new Error(`Finansal analiz yapılamadı: ${error.message}`);
  }
}

/**
 * Parse a credit card statement from PDF or Excel content
 * @param {string} content - The extracted text content from the file
 * @param {string} fileName - Original file name
 * @param {string} fileType - File type (pdf, excel)
 * @returns {Promise<Object>} - Parsed statement with card info, summary, and transactions
 */
export async function parseCreditCardStatement(content, fileName, fileType) {
  try {
    return await callGeminiFunction('parse-credit-card-statement', {
      content,
      fileName,
      fileType,
    });
  } catch (error) {
    console.error('Error parsing credit card statement:', error);
    throw new Error(`Kredi kartı ekstresi çıkarılamadı: ${error.message}`);
  }
}

/**
 * Parse a credit card statement from an image using Gemini Vision
 * @param {string} imageBase64 - Base64 encoded image data
 * @param {string} mimeType - Image MIME type (e.g., 'image/png', 'image/jpeg')
 * @param {string} fileName - Original file name
 * @returns {Promise<Object>} - Parsed statement with card info, summary, and transactions
 */
export async function parseCreditCardStatementFromImage(imageBase64, mimeType, fileName) {
  try {
    return await callGeminiFunction('parse-credit-card-statement-image', {
      imageBase64,
      mimeType,
      fileName,
    });
  } catch (error) {
    console.error('Error parsing credit card statement from image:', error);
    throw new Error(`Görseldan kredi kartı ekstresi çıkarılamadı: ${error.message}`);
  }
}

/**
 * Analyze spending patterns from credit card transactions
 * @param {Object} data - Transactions and statement data
 * @returns {Promise<Object>} - Spending analysis with category breakdown, merchants, trends, and recommendations
 */
export async function analyzeSpending(data) {
  try {
    return await callGeminiFunction('analyze-spending', data);
  } catch (error) {
    console.error('Error analyzing spending:', error);
    throw new Error(`Harcama analizi yapılamadı: ${error.message}`);
  }
}

/**
 * Parse MoodMap natural language input
 * @param {string} text - User's natural language input (e.g., "Spor yaptım, toplantı iyi geçti ama yorgunum")
 * @param {Array} availableTags - List of available tags for matching
 * @returns {Promise<Object>} - Parsed items with categories and suggested tags
 *
 * Returns:
 * {
 *   items: [
 *     { category: 'win', content: 'Spor yaptım', suggestedTag: 'Spor' },
 *     { category: 'win', content: 'Toplantı iyi geçti' },
 *     { category: 'struggle', content: 'Yorgunum' }
 *   ],
 *   suggestedMood: 'good'  // Optional mood suggestion based on content
 * }
 */
export async function parseMoodMapInput(text, availableTags = []) {
  try {
    return await callGeminiFunction('parse-moodmap', {
      text,
      availableTags,
    });
  } catch (error) {
    console.error('Error parsing moodmap input:', error);
    throw new Error(`Giriş anlaşılamadı: ${error.message}`);
  }
}

/**
 * Smart URL scraping using fetch + Gemini for extraction
 * Falls back to basic metadata if full scraping fails
 */
export async function scrapeAndSummarize(url) {
  try {
    // Basic fetch to get the page content
    // Note: In production, you'd want a proper scraping service
    // due to CORS restrictions. This is a simplified version.
    const response = await fetch(url);
    const html = await response.text();

    // Extract basic content (simplified - real implementation would use a proper parser)
    const titleMatch = html.match(/<title>(.*?)<\/title>/i);
    const title = titleMatch ? titleMatch[1] : url;

    // Extract text content (very simplified)
    const textContent = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .substring(0, 10000);

    // Use Gemini to summarize via edge function
    const summary = await summarizeUrlContent(url, textContent);

    return {
      ...summary,
      originalUrl: url,
      rawTitle: title,
    };
  } catch (error) {
    console.error('Error scraping URL:', error);
    // Return basic info on failure
    return {
      title: url,
      summary: 'Unable to fetch content from this URL',
      concepts: [],
      keyTakeaways: [],
      suggestedCluster: 'web-link',
      contentForEmbedding: url,
    };
  }
}

// Export all functions
export default {
  generateEmbedding,
  analyzeConnections,
  describeImage,
  summarizeUrlContent,
  summarizePDF,
  chatWithBrain,
  generateChallenge,
  analyzeGaps,
  generateClusterTag,
  analyzeCrossMapConnections,
  scrapeAndSummarize,
  parseCashflowCommand,
  parsePaymentSchedule,
  parsePaymentScheduleFromImage,
  analyzeCashflow,
  parseCreditCardStatement,
  parseCreditCardStatementFromImage,
  analyzeSpending,
  parseMoodMapInput,
  EMBEDDING_DIMENSION,
};
